import { CommonModule } from '@angular/common';
import { Component, Input, ViewChild, ChangeDetectorRef } from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { LoaderComponent } from '../loader/loader.component';
import { environment } from '../../../../environments/environment';
import { FormsServiceService } from '../../../api/services/forms-service.service';
import { TaskListComponent } from '../../../features/admin/task-list/task-list.component';
import { Router } from '@angular/router';
import { UserPrefsService } from '../../../core/services/user-prefs.service';

@Component({
    selector: 'app-card-type-four',
    imports: [ CommonModule, FormsModule, ReactiveFormsModule ],
    templateUrl: './card-type-four.component.html',
    styleUrl: './card-type-four.component.css'
})
export class CardTypeFourComponent {
  endpoint = environment.api_endpoint;
  fieldsData: any;
  isLoading: boolean = false;
  @ViewChild('TaskListComponent') TaskListComponent!: TaskListComponent;
  constructor(
    private formService: FormsServiceService,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private prefs: UserPrefsService,
  ) {}

  ngOnInit() {
    this.loadData();
  }

  /** Function to load the task list with the formId */
  loadData() {
    this.isLoading = true;
    const payload = {
      source: 'tasks',
      includeTrend: true,
      trendMode: 'value',
      sharedFilters: {},
      cards: [
        {
          id: 'Deployed',
          sequence: 1,
          label: 'Deployed',
          subtitle: 'Live & Active',
          cubeType: 'task_count',
          metric: 'count',
          groupBy: ['status'],
          select: [{ field: 'status', values: ['Deployed'] }],
        },
        {
          id: 'Verified',
          sequence: 2,
          label: 'Verified',
          subtitle: 'Immediate Action',
          cubeType: 'task_count',
          metric: 'count',
          groupBy: ['status'],
          select: [{ field: 'status', values: ['Verified'] }],
        },
        {
          id: 'Audited',
          sequence: 3,
          label: 'Audited',
          subtitle: 'Reviewed & Verified',
          cubeType: 'task_count',
          metric: 'count',
          groupBy: ['status'],
          select: [{ field: 'status', values: ['Audited'] }],
        },
        {
          id: 'Do Now',
          sequence: 4,
          label: 'Do Now',
          subtitle: 'Upcoming Tasks',
          cubeType: 'task_count',
          metric: 'count',
          groupBy: ['priority'],
          select: [{ field: 'priority', values: ['Do Now'] }],
        },
      ],
    };
    this.formService.loadAggregationData(payload).subscribe({
      next: (data) => {
        // this.fieldsData = data.data;
        this.fieldsData = [...data].sort((a, b) => a.sequence - b.sequence);
        this.isLoading = false;
       this.cdr.detectChanges();},
      error: (err) => {
        this.isLoading = false;
       this.cdr.detectChanges();},
    });
  }

  /**
   * Returns UI styles (gradient, label class, and progress bar classes)
   * based on the given status label and current theme.
   */
  getGradient(label: string): string {
    const key = (label || '').toLowerCase();
    const isDark = this.prefs.resolvedTheme() === 'dark';

    const gradients = {
      light: {
        'do now':
          'radial-gradient(circle at top left, #FFD1D1 0%, transparent 60%)',
        'do soon':
          'radial-gradient(circle at top left, #FFE4B3 0%, transparent 60%)',
        pending:
          'radial-gradient(circle at top left, #CFE2FF 0%, transparent 60%)',
        'do later':
          'radial-gradient(circle at top left, #D2F8D2 0%, transparent 60%)',
        deployed:
          'radial-gradient(circle at top left, #CBFFD8 0%, transparent 60%)',
        verified:
          'radial-gradient(circle at top left, #C6E1FF 0%, transparent 60%)',
        audited:
          'radial-gradient(circle at top left, #EFD0FF 0%, transparent 60%)',
        compensated:
          'radial-gradient(circle at top left, #FF9500 0%, transparent 60%)',
        default:
          'radial-gradient(circle at top left, #E0E0E0 0%, transparent 60%)',
      },
      dark: {
        'do now':
          'radial-gradient(circle at top left, rgba(255, 77, 77, 0.45) 0%, rgba(255, 77, 77, 0.25) 25%, transparent 60%)',

        'do soon':
          'radial-gradient(circle at top left, rgba(255, 176, 64, 0.45) 0%, rgba(255, 176, 64, 0.25) 25%, transparent 60%)',

        pending:
          'radial-gradient(circle at top left, rgba(96, 165, 250, 0.45) 0%, rgba(96, 165, 250, 0.25) 25%, transparent 60%)',

        'do later':
          'radial-gradient(circle at top left, rgba(74, 222, 128, 0.45) 0%, rgba(74, 222, 128, 0.25) 25%, transparent 60%)',

        deployed:
          'radial-gradient(circle at top left, rgba(34, 197, 94, 0.45) 0%, rgba(34, 197, 94, 0.25) 25%, transparent 60%)',

        verified:
          'radial-gradient(circle at top left, rgba(59, 130, 246, 0.45) 0%, rgba(59, 130, 246, 0.25) 25%, transparent 60%)',

        audited:
          'radial-gradient(circle at top left, rgba(168, 85, 247, 0.45) 0%, rgba(168, 85, 247, 0.25) 25%, transparent 60%)',

        compensated:
          'radial-gradient(circle at top left, rgba(249, 115, 22, 0.45) 0%, rgba(249, 115, 22, 0.25) 25%, transparent 60%)',

        default:
          'radial-gradient(circle at top left, rgba(148, 163, 184, 0.25) 0%, transparent 60%)',
      },
    };

    const theme = isDark ? 'dark' : 'light';
    return (
      gradients[theme][key as keyof (typeof gradients)[typeof theme]] ||
      gradients[theme].default
    );
  }

  getLabelClass(label: string): string {
    switch ((label || '').toLowerCase()) {
      case 'do now':
        return 'label-do-now';
      case 'do soon':
        return 'label-do-soon';
      case 'pending':
        return 'label-pending';
      case 'do later':
        return 'label-do-later';
      case 'deployed':
        return 'label-deployed';
      case 'verified':
        return 'label-verified';
      case 'audited':
        return 'label-audited';
      case 'compensated':
        return 'label-compensated';
      default:
        return 'label-default';
    }
  }

  getProgressBarContainerClass(label: string): string {
    switch ((label || '').toLowerCase()) {
      case 'do now':
        return 'bg-red-100';
      case 'do soon':
        return 'bg-orange-100';
      case 'pending':
        return 'bg-blue-100';
      case 'do later':
        return 'bg-green-100';
      case 'deployed':
        return 'bg-green-100';
      case 'verified':
        return 'bg-blue-100';
      case 'audited':
        return 'bg-purple-100';
      case 'compensated':
        return 'bg-yellow-100';
      default:
        return 'bg-gray-100';
    }
  }

  getProgressBarFillClass(label: string): string {
    switch ((label || '').toLowerCase()) {
      case 'do now':
        return 'bg-danger';
      case 'do soon':
        return 'bg-warning';
      case 'pending':
        return 'bg-blue-500';
      case 'do later':
        return 'bg-green-500';
      case 'deployed':
        return 'bg-success';
      case 'verified':
        return 'bg-verified';
      case 'audited':
        return 'bg-audited';
      case 'compensated':
        return 'bg-compensated';
      default:
        return 'bg-gray-400';
    }
  }

  navigateToTasks(option: string) {
    const normalizedOption = option.toLowerCase();

    const existingTasksView = JSON.parse(
      sessionStorage.getItem('tasksView') || '{}',
    );

    const updateArrayField = (field: any, value: string) => {
      let arr = field;

      if (arr) {
        arr = Array.isArray(arr) ? arr : [arr];
        if (!arr.includes(value)) {
          arr.push(value);
        }
      } else {
        arr = [value];
      }

      return arr;
    };

    const tasksView: any = { ...existingTasksView };

    if (['audited', 'verified', 'deployed'].includes(normalizedOption)) {
      tasksView.status = updateArrayField(existingTasksView.status, option);
      tasksView.taskStatus = updateArrayField(
        existingTasksView.taskStatus,
        option,
      );
    } else if (
      ['do soon', 'high', 'medium', 'low', 'do now'].includes(normalizedOption)
    ) {
      tasksView.priority = updateArrayField(existingTasksView.priority, option);
      tasksView.taskPriority = updateArrayField(
        existingTasksView.taskPriority,
        option,
      );
    }

    sessionStorage.setItem('tasksView', JSON.stringify(tasksView));

    this.router.navigate(['/tasks']);
  }
}
